// AdminAnnouncements.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Tag,
  Space,
  message,
  Popconfirm,
  Divider,
  Typography,
  theme,
  Segmented,
  ConfigProvider
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import adminAxios from './adminAxios';
import { resolveFrontendApiBase } from '../utils/apiBase';
import AnnouncementBanner, {
  AnnouncementAppearance,
  AnnouncementBannerData,
  resolveVisuals
} from '../components/AnnouncementBanner';
import './AdminContentEditors.css';

const { Option } = Select;
const { Text } = Typography;

const API_BASE = resolveFrontendApiBase();

type Appearance = AnnouncementAppearance;

type Announcement = {
  id: string;
  title?: string | null;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  linkText?: string | null;
  linkUrl?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  updatedAt?: string | null;
  isActive: boolean;
  priority?: number;
  appearance?: Appearance | null;
};

type FormValues = {
  id?: string;
  title?: string;
  message: string;
  type: Announcement['type'];
  linkText?: string;
  linkUrl?: string;
  isActive: boolean;
  priority: number;
  appearance?: Appearance;
  startsAt?: string | null;
  endsAt?: string | null;
};

const emptyAppearance: Appearance = {
  variant: 'announcement',
  showIcon: true,
  closable: true,
  align: 'center',
  iconName: 'info',
  bgColor: null,
  borderColor: null,
  textColor: null
};

const AdminAnnouncements: React.FC = () => {
  const [rows, setRows] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [startVal, setStartVal] = useState<Dayjs | null>(null);
  const [endVal, setEndVal] = useState<Dayjs | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Live preview state mirrors form values
  const [previewValues, setPreviewValues] = useState<FormValues>({
    message: '',
    type: 'info',
    isActive: true,
    priority: 0,
    appearance: { ...emptyAppearance },
    title: '',
    linkText: '',
    linkUrl: '',
    startsAt: null,
    endsAt: null
  });

  const [form] = Form.useForm<FormValues>();
  const { token } = theme.useToken();
  const modalOpenRef = useRef<boolean>(false);
  const [previewThemeMode, setPreviewThemeMode] = useState<'app' | 'light' | 'dark'>('app');

  const fetchAll = async (): Promise<void> => {
    setLoading(true);
    setInlineError(null);
    try {
      const res = await adminAxios.get<{ items: Announcement[] }>(`${API_BASE}/announcements`);
      setRows(res.data.items);
    } catch (e) {
      console.error(e);
      setInlineError('Failed to load announcements');
      message.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchAll();
  }, []);

  const openCreate = (): void => {
    setEditing(null);
    form.resetFields();
    setStartVal(null);
    setEndVal(null);

    const initial: FormValues = {
      isActive: true,
      type: 'info',
      appearance: { ...emptyAppearance },
      priority: 0,
      startsAt: null,
      endsAt: null,
      title: '',
      linkText: '',
      linkUrl: '',
      message: ''
    };

    form.setFieldsValue(initial);
    setPreviewValues(initial);
    setEditorOpen(true);
  };

  const openEdit = (row: Announcement): void => {
    setEditing(row);
    form.resetFields();

    const start = row.startsAt ? dayjs(row.startsAt) : null;
    const end = row.endsAt ? dayjs(row.endsAt) : null;

    setStartVal(start);
    setEndVal(end);

    const initial: FormValues = {
      id: row.id,
      title: row.title || '',
      message: row.message,
      type: row.type,
      linkText: row.linkText || '',
      linkUrl: row.linkUrl || '',
      isActive: row.isActive,
      priority: row.priority ?? 0,
      appearance: {
        ...emptyAppearance,
        ...(row.appearance || {})
      },
      startsAt: row.startsAt ?? null,
      endsAt: row.endsAt ?? null
    };

    form.setFieldsValue(initial);
    setPreviewValues(initial);
    setEditorOpen(true);
  };

  const save = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      setInlineError(null);

      const payload: Partial<Announcement> = {
        title: values.title || null,
        message: values.message,
        type: values.type,
        linkText: values.linkText || null,
        linkUrl: values.linkUrl || null,
        isActive: values.isActive,
        priority: values.priority ?? 0,
        appearance: values.appearance || emptyAppearance,
        startsAt: values.startsAt ?? null,
        endsAt: values.endsAt ?? null
      };

      if (editing) {
        await adminAxios.put(`${API_BASE}/announcements/${editing.id}`, payload);
        message.success('Announcement updated');
      } else {
        await adminAxios.post(`${API_BASE}/announcements`, payload);
        message.success('Announcement created');
      }

      setEditorOpen(false);
      await fetchAll();
    } catch (e: any) {
      if (e?.errorFields) return;
      console.error(e);
      setInlineError('Save failed');
      message.error('Save failed');
    }
  };

  const toggleActive = async (row: Announcement, makeActive: boolean): Promise<void> => {
    try {
      setInlineError(null);
      if (makeActive) {
        await adminAxios.post(`${API_BASE}/announcements/${row.id}/activate`);
      } else {
        await adminAxios.post(`${API_BASE}/announcements/${row.id}/deactivate`);
      }
      message.success(makeActive ? 'Activated' : 'Deactivated');
      await fetchAll();
    } catch (e) {
      console.error(e);
      setInlineError('Operation failed');
      message.error('Operation failed');
    }
  };

  const remove = async (row: Announcement): Promise<void> => {
    try {
      setInlineError(null);
      await adminAxios.delete(`${API_BASE}/announcements/${row.id}`);
      message.success('Announcement deleted');
      await fetchAll();
    } catch (e) {
      console.error(e);
      setInlineError('Delete failed');
      message.error('Delete failed');
    }
  };

  const columns: ColumnsType<Announcement> = [
    { title: 'Title', dataIndex: 'title', key: 'title', render: (v: string | null) => v || <i>Untitled</i> },
    { title: 'Type', dataIndex: 'type', key: 'type', render: (t: Announcement['type']) => <Tag>{t}</Tag> },
    {
      title: 'Variant',
      key: 'variant',
      render: (_: unknown, r: Announcement) => <Tag color="blue">{r.appearance?.variant || 'announcement'}</Tag>
    },
    { title: 'Priority', dataIndex: 'priority', key: 'priority' },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (v: boolean) => (v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>)
    },
    {
      title: 'Window',
      key: 'win',
      render: (_: unknown, r: Announcement) =>
        r.startsAt || r.endsAt ? (
          <span>
            {r.startsAt ? dayjs(r.startsAt).format('YYYY-MM-DD HH:mm') : '...'} →{' '}
            {r.endsAt ? dayjs(r.endsAt).format('YYYY-MM-DD HH:mm') : '...'}
          </span>
        ) : (
          <i>None</i>
        )
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v?: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, r: Announcement) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          {r.isActive ? (
            <Button size="small" danger onClick={() => toggleActive(r, false)}>Deactivate</Button>
          ) : (
            <Button size="small" type="primary" onClick={() => toggleActive(r, true)}>Activate</Button>
          )}
          <Popconfirm
            title="Delete this announcement?"
            description="This cannot be undone."
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => remove(r)}
          >
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Sync preview with form changes in realtime
  const handleValuesChange = (_: Partial<FormValues>, allValues: FormValues): void => {
    setPreviewValues(allValues);
  };

  // Derived preview props
  const previewBannerData: AnnouncementBannerData = useMemo(() => ({
    id: previewValues.id || 'preview',
    title: (previewValues.title || '').trim() || 'Announcement',
    message: (previewValues.message || '').trim() || 'Your message will appear here.',
    type: previewValues.type,
    linkText: (previewValues.linkText || '').trim() || undefined,
    linkUrl: (previewValues.linkUrl || '').trim() || undefined,
    appearance: previewValues.appearance || emptyAppearance,
  }), [
    previewValues.id,
    previewValues.title,
    previewValues.message,
    previewValues.type,
    previewValues.linkText,
    previewValues.linkUrl,
    previewValues.appearance,
  ]);

  const previewTokens = useMemo(() => {
    if (previewThemeMode === 'app') {
      return token;
    }
    const algorithm = previewThemeMode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm;
    return theme.getDesignToken({
      algorithm,
    });
  }, [previewThemeMode, token]);

  const previewVisuals = useMemo(() => (
    resolveVisuals(previewBannerData.appearance, previewBannerData.type, previewTokens)
  ), [previewBannerData.appearance, previewBannerData.type, previewTokens]);

  const previewBannerNode = (
    <AnnouncementBanner
      data={previewBannerData}
      onClose={() => { /* keep preview visible */ }}
    />
  );

  const themedPreviewBanner = previewThemeMode === 'app'
    ? previewBannerNode
    : (
      <ConfigProvider
        theme={{
          algorithm: previewThemeMode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
        }}
      >
        {previewBannerNode}
      </ConfigProvider>
    );

  const previewStartDisplay = useMemo(() => {
    const s = previewValues.startsAt ? dayjs(previewValues.startsAt) : null;
    return s ? s.format('YYYY-MM-DD HH:mm') : '...';
  }, [previewValues.startsAt]);

  const previewEndDisplay = useMemo(() => {
    const e = previewValues.endsAt ? dayjs(previewValues.endsAt) : null;
    return e ? e.format('YYYY-MM-DD HH:mm') : '...';
  }, [previewValues.endsAt]);

  return (
    <div className="admin-content-editor">
      <section className="admin-content-editor-header" aria-labelledby="admin-announcements-title">
        <div>
          <div className="admin-content-editor-kicker">Content editor</div>
          <h2 id="admin-announcements-title">Announcements</h2>
          <p>Create, schedule, preview, and manage in-app announcements.</p>
        </div>
      </section>
      {inlineError && (
        <Alert
          className="admin-content-editor-inline-alert"
          type="error"
          showIcon
          message="Announcement editor needs attention"
          description={inlineError}
        />
      )}
    <Card title="Announcement List" className="admin-content-editor-card">
      <div className="admin-content-editor-toolbar">
        <Button type="primary" onClick={openCreate}>New Announcement</Button>
        <Button onClick={fetchAll}>Refresh</Button>
      </div>
      <Table rowKey="id" loading={loading} dataSource={rows} columns={columns} pagination={{ pageSize: 10 }} scroll={{ x: 920 }} />

      <Modal
        className="admin-content-editor-modal"
        title={editing ? 'Edit Announcement' : 'New Announcement'}
        open={editorOpen}
        onOk={save}
        onCancel={() => setEditorOpen(false)}
        width={1200}
        destroyOnClose
        afterOpenChange={(open) => { modalOpenRef.current = open; }}
      >
        {/* Sticky preview at the very top of the modal body */}
        <div
          className="admin-content-editor-sticky-preview"
        >
          <Card
            size="small"
            title="Live Preview"
            bodyStyle={{ paddingTop: 16 }}
            extra={(
              <Space size={8} align="center">
                <Text type="secondary">Theme:</Text>
                <Segmented
                  options={[
                    { label: 'App', value: 'app' },
                    { label: 'Light', value: 'light' },
                    { label: 'Dark', value: 'dark' },
                  ]}
                  value={previewThemeMode}
                  onChange={(value) => setPreviewThemeMode(value as 'app' | 'light' | 'dark')}
                />
              </Space>
            )}
            className="admin-content-editor-card"
          >
            {themedPreviewBanner}

            <div style={{ fontSize: 12, lineHeight: 1.6 }}>
              <div>
                <Text type="secondary">Window:</Text>{' '}
                <Text>
                  {previewValues.startsAt || previewValues.endsAt
                    ? `${previewStartDisplay} → ${previewEndDisplay}`
                    : 'None'}
                </Text>
              </div>
              <div>
                <Text type="secondary">Active:</Text>{' '}
                <Tag color={previewValues.isActive ? 'green' : undefined}>
                  {previewValues.isActive ? 'Active' : 'Inactive'}
                </Tag>
              </div>
              <div>
                <Text type="secondary">Variant:</Text>{' '}
                <Tag color="blue">{previewValues.appearance?.variant || 'announcement'}</Tag>
              </div>
              <div>
                <Text type="secondary">Priority:</Text> <Text>{previewValues.priority ?? 0}</Text>
              </div>
              <div>
                <Text type="secondary">Visuals:</Text>{' '}
                <Text>
                  surface {previewVisuals.background}, accent {previewVisuals.accentColor}, border {previewVisuals.borderColor}, text {previewVisuals.textColor}
                </Text>
              </div>
            </div>
          </Card>
          <Divider style={{ margin: '0 0 12px 0' }} />
        </div>

        {/* The editor form scrolls under the sticky preview */}
        <Form
          form={form}
          className="admin-content-editor-form"
          layout="vertical"
          onValuesChange={handleValuesChange}
        >
          <Form.Item name="title" label="Title">
            <Input placeholder="Optional headline shown above the message" />
          </Form.Item>

          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Message is required' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="type"
            label="Alert Type"
            rules={[{ required: true, message: 'Type is required' }]}
            initialValue="info"
          >
            <Select>
              <Option value="info">info</Option>
              <Option value="success">success</Option>
              <Option value="warning">warning</Option>
              <Option value="error">error</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Active Window">
            <Space>
              <DatePicker
                showTime
                allowClear
                format="YYYY-MM-DD HH:mm"
                placeholder="Start"
                value={startVal ?? undefined}
                onChange={(v) => {
                  setStartVal(v ?? null);
                  form.setFieldsValue({ startsAt: v ? v.toISOString() : null });
                  if (v && endVal && endVal.isBefore(v)) {
                    setEndVal(null);
                    form.setFieldsValue({ endsAt: null });
                  }
                }}
                onOk={(v) => {
                  if (v) {
                    setStartVal(v);
                    form.setFieldsValue({ startsAt: v.toISOString() });
                    if (endVal && endVal.isBefore(v)) {
                      setEndVal(null);
                      form.setFieldsValue({ endsAt: null });
                    }
                  }
                }}
                getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
              />
              <span>to</span>
              <DatePicker
                showTime
                allowClear
                format="YYYY-MM-DD HH:mm"
                placeholder="End"
                value={endVal ?? undefined}
                disabledDate={(current) => {
                  return !!(startVal && current && current.isBefore(startVal));
                }}
                onChange={(v) => {
                  setEndVal(v ?? null);
                  form.setFieldsValue({ endsAt: v ? v.toISOString() : null });
                }}
                onOk={(v) => {
                  if (v) {
                    setEndVal(v);
                    form.setFieldsValue({ endsAt: v.toISOString() });
                  }
                }}
                getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
              />
            </Space>
          </Form.Item>

          {/* Hidden ISO fields that are saved to the API */}
          <Form.Item name="startsAt" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="endsAt" hidden>
            <Input />
          </Form.Item>

          <Form.Item label="CTA Text" name="linkText">
            <Input />
          </Form.Item>
          <Form.Item label="CTA URL" name="linkUrl">
            <Input />
          </Form.Item>

          <Form.Item label="Priority" name="priority" initialValue={0}>
            <Select>
              {Array.from({ length: 11 }).map((_, i) => (
                <Option key={i} value={i}>{i}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Active" name="isActive" valuePropName="checked" initialValue>
            <Switch />
          </Form.Item>

          <Card size="small" title="Appearance" className="admin-content-editor-card">
            <div className="admin-content-editor-swatches" aria-label="Token color reference">
              <span style={{ background: 'var(--text-info)' }} />
              <span style={{ background: 'var(--surface-base)' }} />
              <span style={{ background: 'var(--border-subtle)' }} />
              <span style={{ background: 'var(--text-main)' }} />
              <Text type="secondary">Token reference is shown above; live preview validates the selected colors.</Text>
            </div>
            <Form.Item label="Variant" name={['appearance', 'variant']} initialValue="announcement">
              <Select>
                <Option value="announcement">announcement</Option>
                <Option value="update">update</Option>
                <Option value="important">important</Option>
              </Select>
            </Form.Item>

            <Form.Item label="Icon Name" name={['appearance', 'iconName']} initialValue="info">
              <Select>
                <Option value="info">info</Option>
                <Option value="success">success</Option>
                <Option value="warning">warning</Option>
                <Option value="error">error</Option>
                <Option value="notification">notification</Option>
                <Option value="bolt">bolt</Option>
                <Option value="star">star</Option>
                <Option value="bullhorn">bullhorn</Option>
              </Select>
            </Form.Item>

            <Form.Item label="Show Icon" name={['appearance', 'showIcon']} valuePropName="checked" initialValue>
              <Switch />
            </Form.Item>

            <Form.Item label="Closable" name={['appearance', 'closable']} valuePropName="checked" initialValue>
              <Switch />
            </Form.Item>

            <Form.Item label="Align" name={['appearance', 'align']} initialValue="center">
              <Select>
                <Option value="left">left</Option>
                <Option value="center">center</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Accent Color"
              name={['appearance', 'bgColor']}
              extra="Used for the thin accent line. The banner surface follows the current app theme."
              className="admin-content-editor-color-field"
            >
              <Input placeholder="var(--accent-primary) or any CSS color" />
            </Form.Item>
            <Form.Item
              label="Border Color"
              name={['appearance', 'borderColor']}
              extra="Prefer token values such as var(--border-info); verify custom values in both themes."
              className="admin-content-editor-color-field"
            >
              <Input placeholder="var(--border-subtle) or any CSS color" />
            </Form.Item>
            <Form.Item
              label="Text Color"
              name={['appearance', 'textColor']}
              extra="Use token text colors when possible so contrast stays readable."
              className="admin-content-editor-color-field"
            >
              <Input placeholder="var(--text-main) or any CSS color" />
            </Form.Item>
          </Card>
        </Form>
      </Modal>
    </Card>
    </div>
  );
};

export default AdminAnnouncements;
